import EncounterDetail from "issara-sdk/apis/EncounterDetail_core";

import RehabOrderDetail from "issara-sdk/apis/RehabOrderDetail_apps_RHB";
import RehabOrderWorkList from "issara-sdk/apis/RehabOrderWorkList_apps_RHB";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import { beStringToAdString } from "react-lib/utils/dateUtils";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  EDIT_COMPLETE: "EDIT_COMPLETE",
  EDIT_PHYSIOLOGIST: "EDIT_PHYSIOLOGIST",
  SEARCH: "SEARCH",
  SELECT: "GO_TO_ENCOUNTER",
} as const;

export const SEQUENCE_NAME = "RehabWorkList" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  RehabWorkListSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    filter: FilterType;
    items: RehabWorkListType[];
  }> | null;
}>;

export type RehabWorkListType = {
  continue_start_date?: string | null;
  doctor_full_name: string;
  en_division: string;
  en_number: string;
  en_started: string;
  en_status: string;
  en_type: string;
  hn: string;
  orders: {
    hide_from_queue: boolean;
    is_appointment: boolean;
    is_new: boolean;
    order_id: number;
    physiotherapist: number | null;
    physiotherapist_full_name: string | null;
  }[];
  out_perform_div_name: string;
  patient_name: string;
};

export type PickedState = PickMainState<
  | "buttonLoadCheck"
  | "django"
  | "errorMessage"
  | "initialLayout"
  | "searchedItemListWithKey"
  | "selectedDivision"
  | "selectedRehabWorkList"
>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

type FilterType = Partial<{
  encounter_division: number;
  encounter_type: "IPD" | "OPD";
  include_complete: boolean;
  order_time: string;
  patient: number;
  physiotherapist: number | null;
  physiotherapist_full_name?: string;
  to_department_for_physiotherapist: number;
}>;

export type EditCompleteParamsType = {
  hide_from_queue: boolean;
  index: number;
  orders: RehabWorkListType["orders"];
};

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.EDIT_COMPLETE]: EditCompleteParamsType;
  [ACTIONS.EDIT_PHYSIOLOGIST]: {
    orders: RehabWorkListType["orders"];
    physiotherapist: number | null;
    onSuccess: () => void;
  };
  [ACTIONS.SELECT]: { data: RehabWorkListType; encounterId: number };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  RehabWorkListSequence: null,
};

export const DataInitial: Data = {};

const Masters = [["division", {}]] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    {
      RehabWorkListSequence: {
        ...state.RehabWorkListSequence,
        sequenceIndex: "Action",
        filter: {
          physiotherapist: state.django?.user?.id,
          physiotherapist_full_name: state.django?.user?.full_name,
          to_department_for_physiotherapist: state.selectedDivision?.id,
        },
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.EDIT_COMPLETE]: HandleEditComplete,
    [ACTIONS.EDIT_PHYSIOLOGIST]: HandleEditPhysiologist,
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.SELECT]: HandleSelect,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<unknown, Promise<void>> = async (controller) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const filter = state.RehabWorkListSequence?.filter ?? {};

  const [result] = await RehabOrderWorkList.get({
    apiToken: controller.apiToken,
    params: {
      ...filter,
      include_complete: !!filter.include_complete,
      order_time: beStringToAdString(filter.order_time || ""),
    },
  });

  state = controller.getState();

  controller.setState({
    RehabWorkListSequence: {
      ...state.RehabWorkListSequence,
      items: result?.items || [],
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};

const HandleEditPhysiologist: Handler<Params<Actions["EDIT_PHYSIOLOGIST"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.EDIT_PHYSIOLOGIST]: BLC_STATUS.LOADING },
  });

  const [, error] = await EditRehabOrderDetail(controller, {
    ...params,
    data: { action: "EDIT", physiotherapist: params.physiotherapist },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.EDIT_PHYSIOLOGIST]: null },
  });

  await HandleSearch(controller);

  params.onSuccess();
};

const HandleEditComplete: Handler<Params<Actions["EDIT_COMPLETE"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const btnKey = `${BTN_ACTS.EDIT_COMPLETE}_${params.index}`;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const [, error] = await EditRehabOrderDetail(controller, {
    ...params,
    data: { 
      action: "EDIT_PERFORM", 
      hide_from_queue: params.hide_from_queue, 
      edit_perform_note: "Complete (hide_from_queue)" },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, action: btnKey, btnError: null, error });

    return;
  }

  await HandleSearch(controller);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
  });
};

const HandleSelect: Handler<Params<Actions["SELECT"]>> = async (controller, params) => {
  const [encounter] = await EncounterDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.encounterId,
  });

  controller.setState({ initialLayout: 1 });

  controller.handleEvent({
    message: "SelectEncounter" as any,
    params: {
      encounter: { ...encounter },
      goToMenu: "Rehabilitation Treatment Plan",
      goToTab: "treatmentPerformRecord",
    },
  });

  controller.setState({ selectedRehabWorkList: params.data });
};

/** ====================================================  */
/**                         APIS                          */
/** ====================================================  */
const EditRehabOrderDetail: Handler<
  {
    data: { action: string; hide_from_queue?: boolean; physiotherapist?: number | null; edit_perform_note?: string };
    orders: RehabWorkListType["orders"];
  },
  Promise<[unknown, unknown]>
> = async (controller, params) => {
  const promiseArr = params.orders.map(
    (item) =>
      RehabOrderDetail.patch({
        apiToken: controller.apiToken,
        data: { ...params.data },
        pk: item.order_id,
      }) as [unknown, unknown]
  );

  const response = await Promise.all(promiseArr);

  const isError = response.some((item: any) => item[1]);

  return isError
    ? [null, response.map((item) => item[1])]
    : [response.map((item) => item[0]), null];
};
